import React from 'react';
import { Grid, Box } from '@mui/material';
import CaptionValue from '../../CaptionValue/CaptionValue';
import PagesStepper from '../../PagesStepper';
import useTranslate from '../../../Hooks/useTranslate';
import resolveValuePath from '../../../Helpers/resolveValuePath';
import { resolveIcon } from '../../../Utils/constants/icons';
import { renderColumnValue } from '../columns/resolveCard';
import { useBindingScopes } from '../binding/scopes';
import { satisfies } from '../binding/predicate';
import { entryComponent } from './EntryRegistry';
import { useDetailData, DetailDataProvider } from './DetailData';

/** How wide an entry sits when it does not say: the whole row. */
const DEFAULT_SIZE = { xs: 12 };

/** The room a region leaves around its entries, and between them, where it does not say. */
const DEFAULT_PADDING = '16px';
const DEFAULT_SPACING = 2;

/** How the captions of an emphasised entry are drawn. */
const EMPHASIS = { fontWeight: 'bold', textTransform: 'capitalize' };

/** How large an entry's value is drawn, by the scale it declares. */
const SCALES = {
  small: { fontSize: '14px' },
  medium: {},
  large: { fontSize: '20px' },
  heading: { fontSize: '24px', fontWeight: 500 },
};

/** How an entry's caption is drawn beside a value of that scale. */
const CAPTION_SCALES = {
  small: { fontSize: '14px' },
  medium: {},
  large: {},
  heading: {},
};

/** Where an entry sits in the room it was given. */
const ALIGNED_ITEMS = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

/** How its wording runs, so a value under a caption lines up with it. */
const ALIGNED_TEXT = { start: 'left', center: 'center', end: 'right' };

/** How large the icon before an entry is drawn, so it keeps the weight of the value beside it. */
const ICON_SCALES = {
  small: '16px',
  medium: '20px',
  large: '24px',
  heading: '28px',
};

/**
 * How a cell of the grid holds what is in it, where the entry says.
 *
 * A centred entry is centred against its neighbours as well as within itself, so a ring between two
 * stacked columns stands level with the middle of them rather than at the top of the row.
 */
const placement = (align) =>
  align
    ? {
        display: 'flex',
        flexDirection: 'column',
        alignItems: ALIGNED_ITEMS[align],
        ...(align === 'center' ? { alignSelf: 'center' } : {}),
      }
    : undefined;

/**
 * A region's keys as a grid of captioned values.
 *
 * The region says how much room it leaves: `padding` around the grid, `spacing` between the entries
 * in it, in theme units. `entryDefaults` is what a key added to it starts as, for a region whose
 * entries are alike.
 *
 * Each entry is a list column in every respect - its cell draws the value, its vocabulary names it -
 * so a key configured for the list reads the same here. Beyond that an entry chooses how it sits:
 *
 *   size      - columns to span, as a list column declares them. Defaults to the whole row.
 *   direction - `row` puts the caption beside the value, `column` above it.
 *   emphasis  - draws the caption in the primary colour, for a figure rather than a property.
 *   icon      - drawn before the caption, named as any declared icon is.
 *   scale      - how large the value is drawn: small, medium, large or heading.
 *   align      - where it sits in the room it was given: start, center or end.
 *   noCaption  - draws the value alone, for the ones a caption would only repeat.
 *   colon      - follows the caption with one, for the rows written that way.
 *   captionCase - how the caption is cased, whatever the wording behind it says.
 *   newRow     - starts a row of its own, whatever room the one above had left.
 *   padding    - room around this one, where it wants more or less than its neighbours.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.region - The compiled region, carrying its entries.
 *
 * @returns {React.JSX.Element}
 */
const FieldsGrid = React.memo(function FieldsGrid({ region }) {
  const translate = useTranslate();
  // The whole of what the page publishes stands as the indexes a key may reach through, since a
  // key naming `[$contactIndex]` is naming something the page said.
  const page = useDetailData();
  const { record, editing } = page;
  const scopes = useBindingScopes({ record });

  /** An entry as it is drawn, wherever it sits: in the grid, or stacked inside a group. */
  const drawEntry = (entry) => {
    // While the region is being arranged every entry stands in its place, so one the layout has
    // conditioned away can still be reached to be edited or taken off.
    if (!editing && !satisfies(entry.visibleWhen, scopes)) return null;

    // A kind that draws itself - a rule, a heading, a gap - stands in place of the value a key
    // would have been drawn as.
    const Kind = entryComponent(entry.kind);

    if (Kind) return <Kind entry={entry} />;

    const Icon = entry.icon ? resolveIcon(entry.icon) : null;
    const drawn = renderColumnValue(entry, record, translate, page);
    const empty = drawn === null || drawn === undefined || drawn === '';

    // While the region is being arranged every entry stands in its place, empty or not: one
    // hidden for want of a value is one that cannot be moved, resized or taken off.
    if (empty && !editing && entry.hideWhenEmpty) return null;

    const scale = entry.scale ?? 'medium';
    const label = entry.label ?? entry.id;
    const captioned = (
      <CaptionValue
        direction={entry.direction ?? 'row'}
        gap="4px"
        noWrap={scale !== 'heading'}
        caption={entry.colon ? `${translate(label)}:` : label}
        // A caption that would be empty is left out rather than drawn blank, which would take its
        // line in a column and its gap in a row.
        omitCaption={Boolean(entry.noCaption) || !label}
        captionColor={entry.emphasis ? 'primary' : undefined}
        captionStyle={{
          ...(CAPTION_SCALES[scale] ?? {}),
          ...(entry.emphasis ? EMPHASIS : {}),
          ...(entry.captionCase ? { textTransform: entry.captionCase } : {}),
        }}
        valueStyle={SCALES[scale] ?? {}}
        textAlign={ALIGNED_TEXT[entry.align] ?? undefined}
        value={drawn}
      />
    );

    // An entry without an icon is laid out as it always was: the row an icon needs is only put
    // around the ones that have one. The icon takes the weight of the value beside it, and sits
    // against the first line rather than the middle where that value runs to several.
    return Icon ? (
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'flex-start',
          alignItems:
            entry.direction === 'column' || scale === 'heading'
              ? 'flex-start'
              : 'center',
        }}
      >
        <Icon
          color="action"
          sx={{ fontSize: ICON_SCALES[scale], flex: 'none' }}
        />
        {captioned}
      </Box>
    ) : (
      captioned
    );
  };

  return (
    <Grid
      container
      spacing={region?.spacing ?? DEFAULT_SPACING}
      sx={{
        p: region?.padding ?? DEFAULT_PADDING,
        overflow: 'auto',
        height: '100%',
        alignContent: 'start',
      }}
    >
      {(region?.entries ?? [])
        .filter((entry) => editing || satisfies(entry.visibleWhen, scopes))
        .map((entry) => (
          <React.Fragment key={entry.id}>
            {/* A break the layout asked for, so what follows starts a line of its own. */}
            {entry.newRow && (
              <Grid size={12} sx={{ height: 0, padding: '0!important' }} />
            )}
            <Grid
              size={entry.size ?? DEFAULT_SIZE}
              sx={{
                ...(placement(entry.align) ?? {}),
                // The room this entry leaves around itself, on top of the room the region leaves
                // around all of them.
                ...(entry.padding ? { p: entry.padding } : {}),
              }}
            >
              {/* A group is one cell holding several entries: stacked, so the two that share a
                column stand against what is beside them, or in a row, so the two that belong on
                one line stay on it however tall either grows. */}
              {entry.kind === 'group' ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: entry.direction === 'row' ? 'row' : 'column',
                    gap: entry.gap ?? '8px',
                    ...(entry.direction === 'row'
                      ? {
                          alignItems: 'center',
                          justifyContent:
                            ALIGNED_ITEMS[entry.align] ?? 'flex-start',
                        }
                      : {
                          alignItems: ALIGNED_ITEMS[entry.align] ?? 'stretch',
                        }),
                  }}
                >
                  {(entry.entries ?? []).map((child) => (
                    <React.Fragment key={child.id}>
                      {drawEntry(child)}
                    </React.Fragment>
                  ))}
                </Box>
              ) : (
                drawEntry(entry)
              )}
            </Grid>
          </React.Fragment>
        ))}
    </Grid>
  );
});

/**
 * A region's keys, read either of the record itself or of one of a collection it holds.
 *
 * `source` names a collection - `products`, `metadata` - and the entries are then drawn against one
 * of its items at a time, stepped through beneath them. The entries do not know the difference: what
 * they read is published as the record, so the same keys, cells and widths serve both.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.region - The compiled region, carrying its entries and its source.
 *
 * @returns {React.JSX.Element}
 */
const FieldsWidget = function FieldsWidget({ region }) {
  const page = useDetailData();
  const [shown, setShown] = React.useState(0);

  if (!region?.source) return <FieldsGrid region={region} />;

  const items = resolveValuePath(page?.record, region.source, page) ?? [];
  // A collection that shortens under a page that was open leaves it past the end.
  const current = Math.min(shown, Math.max(items.length - 1, 0));
  const item = items[current] ?? null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DetailDataProvider value={{ ...page, record: item }}>
          <FieldsGrid region={region} />
        </DetailDataProvider>
      </Box>
      {items.length > 1 && (
        <PagesStepper
          max={items.length}
          currentStep={current}
          handleChangeStep={(steps) => setShown(current + steps)}
        />
      )}
    </Box>
  );
};

export default FieldsWidget;
